import React from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import clsx from 'clsx';
import MuiAccordion from '@mui/material/Accordion';
import MuiAccordionSummary from '@mui/material/AccordionSummary';
import MuiAccordionDetails from '@mui/material/AccordionDetails';
import Input from '@mui/material/Input';
import Tooltip from '@mui/material/Tooltip';
import { ArrowBack, KeyboardArrowDown, KeyboardArrowLeft } from '@mui/icons-material';
import DownloadForOfflineOutlinedIcon from '@mui/icons-material/DownloadForOfflineOutlined';
import { toast } from 'react-hot-toast';
import {
  getListedBondDetail,
  calculateCashflow,
  submitBondscannerInterest,
} from './useListedBonds';
import { useBondscannerYieldRange } from './useDownsellingYield';
import {
  ListedBondDetailData,
  CashflowCalculateData,
  BondManagementMember,
  PayoutScheduleRow,
  DownsellingYieldIsin,
} from './bonds.types';
import { getRupee, getRupeeWithDecimal } from '../../../utils/numbers';
import PriceBreakdown from './PriceBreakdown';
import SelectClient from '../SelectClient';
import ClientDrawer from '../../Clients/ClientDrawer/ClientDrawer';
import useGetPartnerProfile from '../../../hooks/useGetPartnerProfile';
import { useAnalytics } from '../../../hooks/useAnalytics';
import { InfoIcon } from '../../../assets/mui';
import { formatAmountWithText } from './constants';

const formatDate = (value?: string | null) => {
  if (!value) return '-';
  const d = new Date(value);
  if (isNaN(d.getTime())) return '-';
  return d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
};

const formatAmount = (value?: string | number | null) => {
  if (value === null || value === undefined || value === '') return '-';
  const num = Number(value);
  if (isNaN(num)) return '-';
  return getRupeeWithDecimal.format(num);
};

const Fact = ({ label, value }: { label: string; value: React.ReactNode }) => (
  <div className="flex flex-col">
    <span className="text-xs font-medium text-stone-500 tracking-wide mb-1">{label}</span>
    <span className="text-sm font-medium text-[#23272F]">{value || '-'}</span>
  </div>
);

const DisclosureRow = ({ label, value }: { label: string; value: React.ReactNode }) => (
  <div className="flex flex-col gap-1">
    <span className="text-xs text-stone-500">{label}</span>
    <span className="text-sm font-medium text-stone-700">{value || '-'}</span>
  </div>
);

function ListedBondDetail() {
  const { isin = '', id = '' } = useParams();
  const navigate = useNavigate();

  const [detail, setDetail] = React.useState<ListedBondDetailData | null>(null);
  const [isLoading, setIsLoading] = React.useState(true);
  const [isError, setIsError] = React.useState(false);

  const [units, setUnits] = React.useState<number>(0);
  const [unitsInput, setUnitsInput] = React.useState<string>('');
  const [unitsError, setUnitsError] = React.useState<string | null>(null);
  const [calc, setCalc] = React.useState<CashflowCalculateData | null>(null);
  const [isCalcLoading, setIsCalcLoading] = React.useState(false);

  // Downselling yield + commission
  const [sellYieldInput, setSellYieldInput] = React.useState<string>('');
  const [sellYield, setSellYield] = React.useState<number>(0);
  const [yieldError, setYieldError] = React.useState<string | null>(null);
  const [baselineCalc, setBaselineCalc] = React.useState<CashflowCalculateData | null>(null);
  const [commission, setCommission] = React.useState<number | null>(null);

  // Client selection (UI only for now)
  const [client, setClient] = React.useState<any>(null);
  const { data: partnerDetails } = useGetPartnerProfile();
  const [isSubmitting, setIsSubmitting] = React.useState(false);
  const [isInterested, setIsInterested] = React.useState(false);
  const { track } = useAnalytics();

  const [expanded, setExpanded] = React.useState<string | false>('ISIN');

  // Downselling yield range (via the normal NetworkManager client).
  // NetworkManager `Call` resolves to the response body, so `isins` is top-level.
  const { data: yieldRangeData } = useBondscannerYieldRange(id);
  const yieldIsins: DownsellingYieldIsin[] =
    yieldRangeData?.isins || yieldRangeData?.data?.isins || [];
  const yieldRange = yieldIsins.find((item) => item.isin === isin) || yieldIsins[0] || null;
  const sellYieldMin = yieldRange?.sellYieldMin ?? 0;
  const sellYieldMax = yieldRange?.sellYieldMax ?? 0;

  // Initialize the yield input to the minimum allowed once the range loads.
  React.useEffect(() => {
    if (yieldRange) {
      setSellYieldInput(String(yieldRange.sellYieldMin));
      setSellYield(yieldRange.sellYieldMin);
      setYieldError(null);
    }
  }, [yieldRange?.sellYieldMin, yieldRange?.isin]);

  // Fetch deal detail on mount.
  React.useEffect(() => {
    let isMounted = true;
    const fetchDetail = async () => {
      try {
        setIsLoading(true);
        setIsError(false);
        const result = await getListedBondDetail(id);
        if (!isMounted) return;
        const data = result?.data;
        setDetail(data);
        const initialUnits = data?.basicDetails?.minUnits || 1;
        setUnits(initialUnits);
        setUnitsInput(String(initialUnits));
      } catch (error) {
        if (isMounted) setIsError(true);
      } finally {
        if (isMounted) setIsLoading(false);
      }
    };
    fetchDetail();
    return () => {
      isMounted = false;
    };
  }, [id]);

  // Baseline returns at the bond's DEFAULT yield (no `yield` field sent).
  // Recomputes whenever units change so commission stays correct per unit count.
  React.useEffect(() => {
    if (!units || units <= 0) return;
    let isMounted = true;
    const fetchBaseline = async () => {
      try {
        const result = await calculateCashflow({
          units,
          isin,
          isinPlatformConfigId: id,
          bankName: '',
          paymentOption: 'upi',
        });
        if (isMounted) setBaselineCalc(result?.data || null);
      } catch (error) {
        // keep previous baseline on failure
      }
    };
    fetchBaseline();
    return () => {
      isMounted = false;
    };
  }, [units, isin, id]);

  // Main calc at the entered (downselling) yield — drives the right panel.
  // Only fires for a valid in-range yield; debounced.
  React.useEffect(() => {
    if (!units || units <= 0) return;
    if (!sellYield || sellYield <= 0) return;
    if (yieldError) return; // skip out-of-range yields, keep last values
    let isMounted = true;
    const timer = setTimeout(async () => {
      try {
        setIsCalcLoading(true);
        const result = await calculateCashflow({
          units,
          isin,
          isinPlatformConfigId: id,
          bankName: '',
          paymentOption: 'upi',
          yield: sellYield,
        });
        if (isMounted) setCalc(result?.data || null);
      } catch (error) {
        // keep previous calc values on failure
      } finally {
        if (isMounted) setIsCalcLoading(false);
      }
    }, 300);
    return () => {
      isMounted = false;
      clearTimeout(timer);
    };
  }, [units, sellYield, yieldError, isin, id]);

  // Commission = (consideration at max sell yield − consideration at min sell
  // yield) × 3%. Two calculate calls at the range edges, independent of the
  // yield the partner has typed in.
  React.useEffect(() => {
    if (!units || units <= 0) return;
    if (!sellYieldMax || !sellYieldMin) return;
    let isMounted = true;
    const fetchCommission = async () => {
      try {
        const baseBody = {
          units,
          isin,
          isinPlatformConfigId: id,
          bankName: '',
          paymentOption: 'upi',
        };
        const [maxRes, minRes] = await Promise.all([
          calculateCashflow({ ...baseBody, yield: sellYieldMax }),
          calculateCashflow({ ...baseBody, yield: sellYieldMin }),
        ]);
        if (!isMounted) return;
        const maxConsideration = Number(maxRes?.data?.totalConsideration);
        const minConsideration = Number(minRes?.data?.totalConsideration);
        if (isNaN(maxConsideration) || isNaN(minConsideration)) {
          setCommission(null);
          return;
        }
        setCommission((minConsideration - maxConsideration) * 0.3);
      } catch (error) {
        // keep previous commission on failure
      }
    };
    fetchCommission();
    return () => {
      isMounted = false;
    };
  }, [units, sellYieldMin, sellYieldMax, isin, id]);

  if (isLoading) {
    return (
      <div className="my-6 max-w-7xl mx-auto">
        <p className="text-base text-stone-500">Loading bond details...</p>
      </div>
    );
  }

  if (isError || !detail) {
    return (
      <div className="my-6 max-w-7xl mx-auto">
        <p className="text-base text-red-500">
          Something went wrong while fetching this bond. Please try again.
        </p>
      </div>
    );
  }

  const { basicDetails, issuerDetails, isinDetails, rating, coupons, documents, payoutSchedule } =
    detail;

  // Panel falls back to baseline (default-yield) values until a downsell calc lands.
  const panelCalc = calc || baselineCalc;
  const minUnits = basicDetails.minUnits ?? panelCalc?.minUnits ?? 1;
  const maxUnits = basicDetails.maxUnits ?? panelCalc?.maxUnits ?? 999999;

  // Payout schedule values from the API are per single unit; scale them by the
  // selected unit count so the table reflects the partner's chosen quantity.
  const scaleByUnits = (value?: string | number | null) => {
    const num = Number(value);
    if (isNaN(num)) return value ?? null;
    return num * (units || 0);
  };

  const commitUnits = (next: number) => {
    const bounded = Math.max(minUnits, Math.min(maxUnits, next));
    setUnits(bounded);
    setUnitsInput(String(bounded));
    setUnitsError(null);
  };

  const handleUnitsInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    if (!/^\d{0,10}$/.test(value)) return;
    setUnitsInput(value);
    const num = Number(value);
    if (value === '' || isNaN(num)) {
      setUnitsError(null);
      return;
    }
    if (num > maxUnits) {
      setUnitsError(`Maximum ${maxUnits} units allowed`);
      return;
    }
    if (num < minUnits) {
      setUnitsError(`Minimum ${minUnits} units required`);
      return;
    }
    setUnitsError(null);
    if (num > 0) {
      setUnits(num);
    }
  };

  const handleSellYieldInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    if (!/^\d*\.?\d*$/.test(value)) return;
    setSellYieldInput(value);
    const num = Number(value);
    if (value === '' || isNaN(num)) {
      setYieldError('Please enter a yield');
      return;
    }
    if (num < sellYieldMin || num > sellYieldMax) {
      setYieldError(`Yield must be between ${sellYieldMin}% and ${sellYieldMax}%`);
      return;
    }
    setYieldError(null);
    setSellYield(num);
  };

  const handleAccordion = (panel: string) => (_e: unknown, isExpanded: boolean) =>
    setExpanded(isExpanded ? panel : false);

  // Yields shown in the panel header.
  const originalYield = baselineCalc?.yield ?? Number(basicDetails.yield);
  const downsellYield = calc?.yield ?? sellYield;

  const handleInvestNow = async () => {
    const profile = partnerDetails?.data?.profile;
    const investData = {
      ipcId: id,
      isin,
      issuerName: basicDetails.issuerName,
      originalYield,
      downsellingYield: downsellYield,
      partnerName: `${profile?.firstName || ''} ${profile?.lastName || ''}`.trim(),
      partnerPublicId: profile?.publicId,
      clientName: client?.name,
      clientPhoneNumber: client?.phone,
      clientEmail: client?.email,
      originalInvAmount: Number(baselineCalc?.totalConsideration),
      finalInvAmount: Number(calc?.totalConsideration),
      partnerCommission: commission,
    };
    try {
      setIsSubmitting(true);
      await submitBondscannerInterest(investData);
      setIsInterested(true);
      toast.success('Interest captured, Rm will reach out to you shortly!');
    } catch (e) {
      toast.error('Failed to submit interest');
    } finally {
      setIsSubmitting(false);
    }
  };

  const isListed = (isinDetails.nature || '').toLowerCase().includes('listed');

  return (
    <div className="my-6 max-w-7xl mx-auto">
      <button
        type="button"
        onClick={() => navigate(-1)}
        className="bg-green-700/10 flex items-center justify-center mb-6 p-1.5 h-9 w-9 rounded-full text-green-700"
      >
        <ArrowBack fontSize="small" />
      </button>
      <div className="flex flex-col md:flex-row gap-8 min-h-screen relative">
        {/* Left section */}
        <div className="w-full md:w-3/5">
          {/* Header */}
          <div className="flex items-center gap-4 mb-5">
            <img
              src={basicDetails.logoUrl}
              alt={basicDetails.brandName || basicDetails.issuerName}
              className="w-12 h-12 rounded object-contain bg-white border border-zinc-200"
            />
            <div>
              <h1 className="text-xl font-semibold text-[#152420]">{basicDetails.issuerName}</h1>
              <p className="text-sm text-stone-500">ISIN: {basicDetails.isin || isin}</p>
            </div>
          </div>

          {/* Key Highlights */}
          <div className="grid grid-cols-3 gap-4 mb-5">
            <Fact label="Yield" value={`${sellYieldInput}%`} />
            <Fact label="Rating" value={rating?.rating} />
            <Fact
              label="Min Invest"
              value={`~₹${formatAmountWithText(basicDetails.minInvestmentAmount || 0)}`}
            />
          </div>

          {/* Listed badge */}
          {isListed && (
            <div className="bg-[#E6F0E8] text-green-800 text-sm font-medium rounded-md px-3 py-2 mb-5 inline-block">
              Public Listed Company
            </div>
          )}

          {/* Quick facts */}
          <div className="grid grid-cols-2 sm:grid-cols-[1.5fr,1.7fr,1.5fr] gap-4 bg-[#F5F5F3] rounded-lg px-5 py-4 mb-6">
            <Fact label="Interest" value={payoutSchedule?.interestPayoutFrequency} />
            <Fact
              label="Principal"
              value={payoutSchedule?.principlePayoutFrequency || 'At maturity'}
            />
            {/* {isinDetails.callOptionMaturity && (
              <Fact label="Call option date" value={formatDate(isinDetails.callOptionMaturity)} />
            )} */}
            <Fact label="Seniority" value={isinDetails.seniority} />
          </div>

          {/* Security Cover */}
          {isinDetails.securityCover && (
            <div className="bg-[#F2F6FA] rounded-lg px-5 py-4 mb-6">
              <span className="text-xs font-medium text-stone-500 tracking-wide">
                Security Cover
              </span>
              <div className="text-2xl font-semibold text-blue-700">
                {isinDetails.securityCover}x
              </div>
            </div>
          )}

          {/* Payout Schedule */}
          {payoutSchedule?.data?.length > 0 && (
            <div className="border border-stone-200 rounded-lg mb-6 overflow-hidden">
              <h3 className="text-stone-700 text-lg font-medium px-5 py-4 border-b border-stone-200">
                Payout Schedule
              </h3>
              <div className="max-h-80 overflow-y-auto">
                <table className="w-full text-sm">
                  <thead className="bg-[#F5F5F3] text-stone-500 sticky top-0">
                    <tr>
                      <th className="text-left font-medium px-5 py-3">Sr. No</th>
                      <th className="text-left font-medium px-5 py-3">Date</th>
                      <th className="text-right font-medium px-5 py-3">Interest</th>
                      <th className="text-right font-medium px-5 py-3">Principal</th>
                      <th className="text-right font-medium px-5 py-3">Total Cashflow</th>
                    </tr>
                  </thead>
                  <tbody>
                    {payoutSchedule.data.map((row: PayoutScheduleRow, idx: number) => (
                      <tr key={`${row.date}-${idx}`} className="border-t border-stone-100">
                        <td className="px-5 py-3 text-stone-600">{idx + 1}</td>
                        <td className="px-5 py-3 text-stone-600">{formatDate(row.date)}</td>
                        <td className="px-5 py-3 text-right text-stone-700">
                          {formatAmount(scaleByUnits(row.interest))}
                        </td>
                        <td className="px-5 py-3 text-right text-stone-700">
                          {Number(row.principal) > 0
                            ? formatAmount(scaleByUnits(row.principal))
                            : '₹0'}
                        </td>
                        <td className="px-5 py-3 text-right font-medium text-stone-800">
                          {formatAmount(scaleByUnits(row.total))}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot>
                    <tr className="bg-[#F5F5F3] border-t border-stone-200 font-semibold text-stone-800">
                      <td className="px-5 py-3" colSpan={2}>
                        Total
                      </td>
                      <td className="px-5 py-3 text-right">
                        {formatAmount(scaleByUnits(payoutSchedule.totalInterest))}
                      </td>
                      <td className="px-5 py-3 text-right">
                        {formatAmount(scaleByUnits(payoutSchedule.totalPrincipal))}
                      </td>
                      <td className="px-5 py-3 text-right">
                        {formatAmount(scaleByUnits(payoutSchedule.totalPayout))}
                      </td>
                    </tr>
                  </tfoot>
                </table>
              </div>
              <p className="text-xs italic text-stone-400 px-5 py-3">
                *There is 10% TDS application on the interest component.
              </p>
            </div>
          )}

          {/* About the company */}
          {issuerDetails?.description && (
            <div className="border border-stone-200 rounded-lg p-5 mb-6">
              <h3 className="text-stone-700 text-lg font-medium mb-3">About the company</h3>
              <p className="text-sm text-stone-600 leading-relaxed mb-4">
                {issuerDetails.description}
              </p>
              {issuerDetails.companyWebsiteUrl && (
                <a
                  href={issuerDetails.companyWebsiteUrl}
                  target="_blank"
                  rel="noreferrer"
                  className="text-sm text-green-700 font-medium underline"
                >
                  Visit website
                </a>
              )}

              {issuerDetails.managementTeam?.length > 0 && (
                <div className="mt-6">
                  <h4 className="text-xs font-medium text-stone-500 tracking-wide mb-3">
                    KEY MANAGEMENT PERSONNEL
                  </h4>
                  <div className="flex flex-col gap-3">
                    {issuerDetails.managementTeam.map((member: BondManagementMember) => (
                      <div key={member.id} className="flex items-center gap-3">
                        {member.memberPhoto?.url ? (
                          <img
                            src={member.memberPhoto.url}
                            alt={member.name}
                            className="w-10 h-10 rounded-full object-cover border border-zinc-200"
                          />
                        ) : (
                          <div className="w-10 h-10 rounded-full bg-stone-100 border border-zinc-200" />
                        )}
                        <div>
                          <div className="text-sm font-medium text-stone-700">
                            {member.name}
                            {member.linkedinUrl && (
                              <a
                                href={member.linkedinUrl}
                                target="_blank"
                                rel="noreferrer"
                                className="ml-2 text-xs text-blue-600 underline"
                              >
                                LinkedIn
                              </a>
                            )}
                          </div>
                          <div className="text-xs text-stone-500">{member.designation}</div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {issuerDetails.googleMaps?.locationUrl && (
                <div className="mt-6">
                  <h4 className="text-xs font-medium text-stone-500 tracking-wide mb-2">
                    LOCATION
                  </h4>
                  {issuerDetails.googleMaps.locationImage?.url ? (
                    <a href={issuerDetails.googleMaps.locationUrl} target="_blank" rel="noreferrer">
                      <img
                        src={issuerDetails.googleMaps.locationImage.url}
                        alt="Location"
                        className="rounded-lg border border-zinc-200 max-w-sm"
                      />
                    </a>
                  ) : (
                    <a
                      href={issuerDetails.googleMaps.locationUrl}
                      target="_blank"
                      rel="noreferrer"
                      className="text-sm text-green-700 font-medium underline"
                    >
                      View on map
                    </a>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Other disclosures */}
          <div className="mb-6">
            <h3 className="text-stone-700 text-lg font-medium mb-3">Other disclosures</h3>

            <MuiAccordion
              className="!bg-white border border-stone-200"
              expanded={expanded === 'ISIN'}
              onChange={handleAccordion('ISIN')}
              disableGutters
              sx={{ '&:before': { height: '0px' } }}
              elevation={0}
            >
              <MuiAccordionSummary expandIcon={<KeyboardArrowDown className="text-stone-400" />}>
                <h4 className="text-stone-700 font-medium text-sm">ISIN Details</h4>
              </MuiAccordionSummary>
              <MuiAccordionDetails>
                <div className="grid grid-cols-2 gap-4">
                  <DisclosureRow label="Face Value" value={formatAmount(isinDetails.faceValue)} />
                  <DisclosureRow label="Nature" value={isinDetails.nature} />
                  <DisclosureRow
                    label="Date of Issue"
                    value={formatDate(isinDetails.dateOfIssuance)}
                  />
                  <DisclosureRow label="Mode of Issue" value={isinDetails.modeOfIssuance} />
                  <DisclosureRow label="Debenture Trustee" value={isinDetails.debentureTrustee} />
                  <DisclosureRow label="Yield Type" value={isinDetails.yieldType} />
                  <div className="col-span-2">
                    <DisclosureRow label="Security Name" value={isinDetails.securityName} />
                  </div>
                </div>
              </MuiAccordionDetails>
            </MuiAccordion>

            <MuiAccordion
              className="!bg-white border border-stone-200 border-t-0"
              expanded={expanded === 'RATING'}
              onChange={handleAccordion('RATING')}
              disableGutters
              sx={{ '&:before': { height: '0px' } }}
              elevation={0}
            >
              <MuiAccordionSummary expandIcon={<KeyboardArrowDown className="text-stone-400" />}>
                <h4 className="text-stone-700 font-medium text-sm">Credit Rating</h4>
              </MuiAccordionSummary>
              <MuiAccordionDetails>
                <div className="grid grid-cols-2 gap-4">
                  <DisclosureRow label="Rating" value={rating?.rating} />
                  <DisclosureRow label="Rating Date" value={formatDate(rating?.ratingDate)} />
                  <DisclosureRow label="Rating Agency" value={rating?.ratingAgency} />
                </div>
              </MuiAccordionDetails>
            </MuiAccordion>

            <MuiAccordion
              className="!bg-white border border-stone-200 border-t-0"
              expanded={expanded === 'COUPON'}
              onChange={handleAccordion('COUPON')}
              disableGutters
              sx={{ '&:before': { height: '0px' } }}
              elevation={0}
            >
              <MuiAccordionSummary expandIcon={<KeyboardArrowDown className="text-stone-400" />}>
                <h4 className="text-stone-700 font-medium text-sm">Coupon Details</h4>
              </MuiAccordionSummary>
              <MuiAccordionDetails>
                <div className="grid grid-cols-2 gap-4">
                  <DisclosureRow label="Coupon Rate" value={`${coupons?.couponRate}%`} />
                  <DisclosureRow label="Coupon Type" value={coupons?.couponType} />
                  <DisclosureRow label="Payout Frequency" value={coupons?.payoutFrequency} />
                </div>
              </MuiAccordionDetails>
            </MuiAccordion>

            {/* Document downloads */}
            {documents?.length > 0 && (
              <div className="flex flex-wrap gap-4 mt-4">
                {documents.map((doc) => (
                  <a
                    key={doc.name}
                    href={doc.url}
                    target="_blank"
                    download
                    rel="noreferrer"
                    className="border border-stone-200 rounded-lg py-4 px-4 flex justify-between items-center gap-6 min-w-[220px]"
                  >
                    <span className="text-stone-700 font-medium text-sm">
                      {doc.name.replace(/_/g, ' ')}
                    </span>
                    <DownloadForOfflineOutlinedIcon className="text-stone-500" />
                  </a>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Right section - investment panel */}
        <div className="w-full md:w-2/5 sticky top-24 h-fit">
          <div className="border border-stone-200 rounded-xl bg-white shadow-sm overflow-hidden">
            {/* Header */}
            <div className="flex items-center justify-between px-5 py-4 border-b border-stone-200">
              <span className="text-base font-semibold text-[#23272F]">
                {basicDetails.issuerName}
              </span>
              {/* <span className="text-sm font-semibold flex items-center gap-1.5">
                <span className="text-stone-400">Yield</span>
                {downsellYield !== originalYield && (
                  <span className="text-stone-400 line-through">{originalYield}%</span>
                )}
                <span className="text-green-700">{downsellYield}%</span>
              </span> */}
            </div>

            {/* Units stepper */}
            <div className="px-5 py-5">
              <div className="flex justify-center">
                <span className="text-sm text-stone-500">Units</span>
              </div>

              <div className="flex items-center justify-center gap-4 mt-3">
                <button
                  type="button"
                  className={clsx(
                    'w-8 h-8 rounded-full flex items-center justify-center text-lg bg-green-700 text-white font-semibold',
                    { 'opacity-20 cursor-not-allowed': units <= minUnits },
                  )}
                  disabled={units <= minUnits}
                  onClick={() => commitUnits(units - 1)}
                >
                  -
                </button>
                <Input
                  disableUnderline
                  sx={{
                    fontSize: '16px',
                    fontWeight: 500,
                    width: '120px',
                    border: `1px solid ${unitsError ? '#F87171' : '#D0D5DD'}`,
                    borderRadius: '8px',
                  }}
                  inputProps={{ style: { textAlign: 'center' } }}
                  value={unitsInput}
                  onChange={handleUnitsInput}
                  onBlur={() => commitUnits(Number(unitsInput) || minUnits)}
                />
                <button
                  type="button"
                  className={clsx(
                    'w-8 h-8 rounded-full flex items-center justify-center text-lg bg-green-700 text-white font-semibold',
                    { 'opacity-20 cursor-not-allowed': units >= maxUnits },
                  )}
                  disabled={units >= maxUnits}
                  onClick={() => commitUnits(units + 1)}
                >
                  +
                </button>
              </div>
              {unitsError && <p className="text-xs text-red-500 mt-2 text-center">{unitsError}</p>}
            </div>

            {/* Calculated values */}
            <div className="grid grid-cols-2 gap-4 bg-[#F5F5F3] px-5 py-4">
              <div className="flex flex-col items-center text-center">
                <span className="text-xs font-medium text-stone-500 tracking-wide mb-1">
                  Inv. Amount
                  <Tooltip
                    arrow
                    placement="bottom"
                    title={<PriceBreakdown calc={panelCalc} />}
                    componentsProps={{
                      tooltip: {
                        sx: {
                          bgcolor: '#fff',
                          color: 'inherit',
                          p: 0,
                          maxWidth: 'none',
                          boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
                          border: '1px solid #E7E5E4',
                          borderRadius: '12px',
                        },
                      },
                      arrow: { sx: { color: '#fff' } },
                    }}
                  >
                    <InfoIcon
                      className="text-gray-500 ml-1 cursor-pointer align-middle"
                      sx={{
                        height: '14px',
                        width: '14px',
                      }}
                    />
                  </Tooltip>
                </span>
                <span className="text-base font-semibold text-[#23272F]">
                  {isCalcLoading && !baselineCalc
                    ? '...'
                    : formatAmount(baselineCalc?.totalConsideration)}
                </span>
              </div>
              <div className="flex flex-col items-center text-center">
                <span className="text-xs font-medium text-stone-500 tracking-wide mb-1">
                  Exp. Returns
                </span>
                <span className="text-base font-semibold text-green-700">
                  {isCalcLoading && !panelCalc ? '...' : formatAmount(panelCalc?.expectedReturns)}
                </span>
              </div>
              {panelCalc?.settlementDate && (
                <div className="flex flex-col items-center text-center">
                  <span className="text-xs font-medium text-stone-500 tracking-wide mb-1">
                    Settlement Date
                  </span>
                  <span className="text-sm font-medium text-stone-700">
                    {formatDate(panelCalc.settlementDate)}
                  </span>
                </div>
              )}
              {panelCalc?.maturityDate && (
                <div className="flex flex-col items-center text-center">
                  <span className="text-xs font-medium text-stone-500 tracking-wide mb-1">
                    Maturity Date
                  </span>
                  <span className="text-sm font-medium text-stone-700">
                    {formatDate(panelCalc.maturityDate)}
                  </span>
                </div>
              )}
            </div>

            {/* Commission box */}
            <div className="px-5 pt-5">
              <div className="flex items-center justify-between bg-[#E6F0E8] rounded-lg px-4 py-3">
                <span className="text-sm font-medium text-green-900">Your Commission</span>
                <span className="text-lg font-semibold text-green-800">
                  {commission != null ? `~ ${getRupee.format(commission)}` : '-'}
                </span>
              </div>
            </div>

            {/* Downselling yield input */}
            <div className="px-5 pt-4">
              <label className="text-sm text-stone-500">Yield (%)</label>
              <div
                className={clsx(
                  'flex items-center border bg-gray-100 rounded-lg px-3 py-2 mt-1.5',
                  yieldError ? 'border-red-400' : 'border-stone-300',
                )}
              >
                <input
                  type="text"
                  inputMode="decimal"
                  className="flex-1 min-w-0 outline-none  text-base font-medium text-slate-700"
                  value={sellYieldInput}
                  onChange={handleSellYieldInput}
                  disabled={true}
                />
                <span className="text-stone-400 text-sm font-medium">%</span>
              </div>
              {/* {yieldError ? (
                <p className="text-xs text-red-500 mt-1">{yieldError}</p>
              ) : (
                yieldRange && (
                  <p className="text-xs text-stone-400 mt-1">
                    Allowed range: {sellYieldMin}% – {sellYieldMax}%
                  </p>
                )
              )} */}
            </div>

            {/* Client selection */}
            <div className="px-5 pt-4">
              <SelectClient
                client={client}
                setClient={setClient}
                showAddNewClient
                eventName="Listed_Bond_Client_Select"
              />
            </div>

            {/* Invest now button */}
            <div className="px-5 py-5">
              <button
                type="button"
                className="w-full bg-green-700 text-white py-2.5 rounded-lg font-semibold text-base disabled:bg-green-700/50 disabled:cursor-not-allowed"
                onClick={() => {
                  handleInvestNow();
                }}
                disabled={isInterested || isSubmitting}
              >
                {isInterested ? 'Done, got it!' : "I'm interested"}
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Add new client flow */}
      <ClientDrawer />
    </div>
  );
}

export default ListedBondDetail;
